/*
 * Primitivas de geometria compartilhadas pelos modelos 3D do CMA.
 *
 * Saíram de CmaAsset3dView.vue quando os dois modelos do carrossel foram
 * separados em módulos (ver models/): são funções puras, sem estado e sem
 * dependência de cena, e é isso que permite que o modelo procedural e
 * qualquer modelo futuro as reaproveitem sem passar pelo componente.
 *
 * Todas recebem o material já pronto e devolvem a malha posicionada — a
 * assinatura repetida `(..., material, x, y, z, segments)` é o que deixa os
 * builders do transformador lerem como uma lista de cotas.
 */
import * as THREE from 'three';

/** Volta completa em radianos. Fica aqui porque tanto os modelos quanto a
 *  câmera do viewer contam voltas. */
export const TAU = Math.PI * 2;

/**
 * Decodifica de sRGB o que a three 0.185 decodifica sozinha: todo hex copiado
 * do protótipo (materiais, luzes, realce) tem de passar por aqui, porque a
 * 0.128 usada no projeto não tem color management.
 *
 * Fica neste módulo por ser compartilhada: os dois modelos do carrossel e a
 * iluminação da cena dependem dela para as cores baterem com a referência.
 */
export function srgbColor(hex) {
  return new THREE.Color(hex).convertSRGBToLinear();
}

export const box = (w, h, d, material, x, y, z) => {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  mesh.position.set(x, y, z);
  return mesh;
};

export const cyl = (radius, height, material, x, y, z, segments = 24) => {
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius, height, segments),
    material,
  );
  mesh.position.set(x, y, z);
  return mesh;
};

/** Meia calota (semicírculo em planta), para a ponta de um traço encostar no
 *  corpo dele em vez de invadi-lo: `thetaStart` 0 cobre o lado +X, `Math.PI`
 *  o lado -X. O lado reto fica aberto, de propósito: ele encosta na barra. */
export const halfCyl = (radius, height, material, x, y, z, thetaStart, segments = 16) => {
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius, height, segments, 1, false, thetaStart, Math.PI),
    material,
  );
  mesh.position.set(x, y, z);
  return mesh;
};

export const cylAxis = (radius, height, material, x, y, z, axis, segments = 20) => {
  const mesh = cyl(radius, height, material, x, y, z, segments);
  if (axis === 'x') mesh.rotation.z = Math.PI / 2;
  if (axis === 'z') mesh.rotation.x = Math.PI / 2;
  return mesh;
};

export const ring = (radius, tube, material, x, y, z, segments = 32) => {
  const mesh = new THREE.Mesh(
    new THREE.TorusGeometry(radius, tube, 10, segments),
    material,
  );
  mesh.position.set(x, y, z);
  mesh.rotation.x = -Math.PI / 2;
  return mesh;
};

export const roundedRect = (width, height, cornerRadius, depth, material, x, y, z) => {
  const w = width / 2;
  const h = height / 2;
  const r = cornerRadius;
  const shape = new THREE.Shape();
  shape.moveTo(-w + r, -h);
  shape.lineTo(w - r, -h);
  shape.quadraticCurveTo(w, -h, w, -h + r);
  shape.lineTo(w, h - r);
  shape.quadraticCurveTo(w, h, w - r, h);
  shape.lineTo(-w + r, h);
  shape.quadraticCurveTo(-w, h, -w, h - r);
  shape.lineTo(-w, -h + r);
  shape.quadraticCurveTo(-w, -h, -w + r, -h);
  const geometry = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
  geometry.translate(0, 0, -depth / 2);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(x, y, z);
  return mesh;
};

export const pipeBetween = (a, b, radius, material, segments = 12) => {
  const start = new THREE.Vector3(...a);
  const end = new THREE.Vector3(...b);
  const direction = end.clone().sub(start);
  const mesh = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius, direction.length(), segments),
    material,
  );
  mesh.position.copy(start).add(end).multiplyScalar(0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  return mesh;
};

export const pipeRun = (group, points, radius, material) => {
  for (let i = 0; i < points.length - 1; i += 1) {
    group.add(pipeBetween(points[i], points[i + 1], radius, material));
  }
  points.slice(1, -1).forEach((point) => {
    const elbow = new THREE.Mesh(new THREE.SphereGeometry(radius * 1.08, 12, 8), material);
    elbow.position.set(...point);
    group.add(elbow);
  });
};

export const annulus = (ri, ro, h, material, x, y, z, segments = 40) => {
  const points = [
    new THREE.Vector2(ri, -h / 2),
    new THREE.Vector2(ro, -h / 2),
    new THREE.Vector2(ro, h / 2),
    new THREE.Vector2(ri, h / 2),
    new THREE.Vector2(ri, -h / 2),
  ];
  const mesh = new THREE.Mesh(new THREE.LatheGeometry(points, segments), material);
  mesh.position.set(x, y, z);
  return mesh;
};

/** Nariz da helice (ver buildFanUnit): aponta na direcao em que ela sopra. */
export const cone = (radius, height, material, x, y, z, segments = 20) => {
  const mesh = new THREE.Mesh(
    new THREE.ConeGeometry(radius, height, segments),
    material,
  );
  mesh.position.set(x, y, z);
  return mesh;
};

export const insulator = (length, bodyRadius, shedRadius, sheds, material, segments = 24) => {
  const points = [new THREE.Vector2(bodyRadius * 0.95, 0)];
  const step = length / sheds;
  for (let i = 0; i < sheds; i += 1) {
    const y = i * step;
    points.push(new THREE.Vector2(bodyRadius, y + step * 0.10));
    points.push(new THREE.Vector2(shedRadius, y + step * 0.32));
    points.push(new THREE.Vector2(shedRadius * 0.95, y + step * 0.44));
    points.push(new THREE.Vector2(bodyRadius, y + step * 0.64));
  }
  points.push(new THREE.Vector2(bodyRadius, length));
  points.push(new THREE.Vector2(bodyRadius * 0.88, length + 0.03));
  return new THREE.Mesh(new THREE.LatheGeometry(points, segments), material);
};

export const finBank = (count, gap, geometry, material, ox, oy, oz) => {
  const instanced = new THREE.InstancedMesh(geometry, material, count);
  const matrix = new THREE.Matrix4();
  for (let i = 0; i < count; i += 1) {
    matrix.makeTranslation(ox + i * gap, oy, oz);
    instanced.setMatrixAt(i, matrix);
  }
  instanced.instanceMatrix.needsUpdate = true;
  return instanced;
};
